import React, { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Button } from "reactstrap";
import Loading from "./components/Loading";
import Sidebar from "./components/Sidebar";
import Home from "./views/Home";
import Profile from "./views/Profile";
import Conjuntos from "./views/Conjuntos";
import Login from "./views/Login";
import { useAuth } from "./context/AuthContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/es";
import "./App.scss";
import { AutorizarUsuario, BackendApi } from "./services/HttpBaseService";
import Tablados from "./views/Tablados";
import Concursos from "./views/Concursos";
import Programacion from "./views/Programacion";
import EtapasConcurso from "./views/EtapasConcurso";
import Pencas from "./views/Pencas";
import BackdropWrapper from "./components/BackdropWrapper";
import Pronosticar from "./views/Pronosticar";
import Liguilla from "./views/Liguilla";
import Resultados from "./views/Resultados";
import ResultadosPenca from "./views/ResultadosPenca";
import Notificaciones from "./views/Notificaciones";
import BannersTablados from "./views/BannersTablados";
import Anunciantes from "./views/Anunciantes";
import ModeracionChat from "./views/ModeracionChat";
import ConcursoCambiarEstado from "./views/ConcursoCambiarEstado";
import HabilitarTemporada from "./views/HabilitarTemporada";
import FeedbackBandeja from "./views/FeedbackBandeja";
import Eventos from "./views/Eventos";

/**
 * Este app ahora vive exclusivamente en `admin.carnavalapp.uy` — la web
 * pública (landing, programación pública, /app, /penca, /apk, política de
 * privacidad, etc.) se migró a `carnaval-web` (Next.js en Vercel), y
 * `/liguilla` y `/anuncios` se discontinuaron. No hay rama pública acá: sin
 * sesión, siempre se ve `Login`.
 */
const PageRoutes = () => {
  const { isLoading, isAuthenticated, getIdToken, logout } = useAuth();
  const [reintentar, setReintentar] = useState(1);
  const [autorizado, setAutorizado] = useState(false);
  const [autorizacionError, setAutorizacionError] = useState(null);

  // A diferencia de Auth0, Firebase no deja una sesión SSO externa: logout()
  // limpia el estado en el acto, sin round-trip de navegación — no hace
  // falta persistir el mensaje de error en sessionStorage para sobrevivir un
  // remount.
  const cerrarSesionConError = mensaje => {
    setAutorizacionError(mensaje);
    logout();
  };

  useEffect(() => {
    // No reintentar solo mientras se muestra un error de autorización: el
    // usuario tiene que decidir reautenticarse a mano.
    if (autorizacionError) return;
    if (!isAuthenticated) return;

    if (reintentar <= 5) {
      setAutorizado(null);
      getIdToken()
        .then(data => {
          BackendApi.defaults.headers.common["Authorization"] =
            "Bearer " + data;
          AutorizarUsuario()
            .then(data => {
              if (data.data == false) {
                setAutorizado(false);
                cerrarSesionConError(
                  "Tu usuario no tiene permisos para acceder al Backoffice."
                );
              } else {
                setAutorizado(data.data);
              }
            })
            .catch(err => {
              const status = err?.response?.status;
              if (status == 401) {
                setAutorizado(false);
                cerrarSesionConError(
                  "Tu sesión no es válida o expiró. Iniciá sesión nuevamente."
                );
              } else if (status == 403) {
                // `LoginBackoffice` tiene `[Authorize(Policy = "RequireAdmin")]`
                // — un usuario autenticado sin ese rol nunca llega a devolver
                // `false` en el body (bug real, 2026-09-04): ASP.NET corta
                // antes con 403, así que este caso quedaba solo en el `else`
                // de abajo, reintentando en silencio hasta agotar los 5
                // intentos y mostrar "No se obtuvo respuesta del servidor" —
                // autenticado pero nunca deslogueado, limbo infinito si el
                // usuario reintentaba (la sesión seguía activa).
                setAutorizado(false);
                cerrarSesionConError(
                  "Tu usuario no tiene permisos para acceder al Backoffice."
                );
              } else {
                console.log(err);
                setTimeout(() => setReintentar(reintentar + 1), 1000);
              }
            });
        })
        .catch(err => {
          console.log(err);
          setAutorizado(false);
          setTimeout(() => setReintentar(reintentar + 1), 1000);
        });
    }
  }, [isAuthenticated, getIdToken, reintentar, autorizacionError]);

  if (autorizacionError) {
    return (
      <div className="center-screen flex-col gap-3 text-red-500">
        <p>{autorizacionError}</p>
        <Button
          color="primary"
          onClick={() => setAutorizacionError(null)}
        >
          Iniciar sesión
        </Button>
      </div>
    );
  }

  return (
    <>
      {!isLoading && isAuthenticated && autorizado ? (
        <div>
          <BackdropWrapper />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <BrowserRouter>
              <div className="flex min-h-screen bg-bg-light dark:bg-bg-dark">
                <Sidebar />
                <main className="flex-1 overflow-auto p-4 md:p-6 custom-scrollbar">
                  <Routes>
                    <Route exact path="/" element={<Home />} />
                    <Route exact path="/profile" element={<Profile />} />
                    <Route exact path="/conjuntos" element={<Conjuntos />} />
                    <Route exact path="/concursos" element={<Concursos />} />
                    <Route exact path="/tablados" element={<Tablados />} />
                    <Route exact path="/pencas" element={<Pencas />} />
                    <Route
                      exact
                      path="/programacion"
                      element={<Programacion />}
                    />
                    <Route
                      exact
                      path="/programacion/:_tabladoId"
                      element={<Programacion />}
                    />
                    <Route
                      exact
                      path="/etapas-concurso/:_concursoId"
                      element={<EtapasConcurso />}
                    />
                    <Route
                      exact
                      path="/concurso/:_concursoId/cambiarEstado"
                      element={<ConcursoCambiarEstado />}
                    />
                    <Route
                      exact
                      path="/habilitar-temporada"
                      element={<HabilitarTemporada />}
                    />
                    <Route
                      exact
                      path="/liguilla/:_concursoId"
                      element={<Liguilla />}
                    />
                    <Route
                      exact
                      path="/resultados/:_concursoId"
                      element={<Resultados />}
                    />
                    <Route
                      exact
                      path="/resultados-penca/:_pencaId"
                      element={<ResultadosPenca />}
                    />
                    <Route
                      exact
                      path="/pronosticar/:_pencaId"
                      element={<Pronosticar />}
                    />
                    <Route
                      exact
                      path="/pronosticar/:_pencaId/:_usuarioId"
                      element={<Pronosticar />}
                    />
                    <Route
                      exact
                      path="/notificaciones"
                      element={<Notificaciones />}
                    />
                    <Route
                      exact
                      path="/anunciantes"
                      element={<Anunciantes />}
                    />
                    <Route exact path="/banners" element={<BannersTablados />} />
                    <Route
                      exact
                      path="/moderacion"
                      element={<ModeracionChat />}
                    />
                    <Route exact path="/feedback" element={<FeedbackBandeja />} />
                    <Route exact path="/eventos" element={<Eventos />} />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </main>
              </div>
            </BrowserRouter>
          </LocalizationProvider>
        </div>
      ) : !isLoading && !isAuthenticated ? (
        <Login />
      ) : !isLoading && !autorizado && reintentar >= 5 ? (
        <h3 className="center-screen text-gray-600 dark:text-gray-300">
          No se obtuvo respuesta del servidor
        </h3>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default PageRoutes;
